/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { colors } from "../../../../../components/atoms/color-picker/color-picker";
import { Loading } from "../../../../../components/molecules/modals/modal-document/styled-modal-document";
import { useUser } from "../../../../../context";
import { useFetch } from "../../../../../hooks";
import { useReadBudgetById } from "../../../../../hooks/react-query/use-read-budget-by-id";
import { TProject } from "../../../../../models";
import { manageUser, sectors } from "../../../../../services";
import { updateProjectClosedScope } from "../../../../../services/projects";
import { TUseFetch } from "../../../../../types/TUseFetch";
import { IProject } from "../../../projects-form/projects-form";
import CollabsEquip from "./collabs-equip";
import CollabsEquipEdit from "./collabs-equip-edit";
import { ProfileActivityErrorDialog } from "./profile-activity-error-dialog";

type TEquipTab = {
    project: TProject;
    getProject: TUseFetch;
};

type TUser = {
    id_e: string;
    avatar?: string;
    name?: string;
    role?: string;
    Permissions: string[];
};

type TSector = {
    id_e: string;
    Projects?: [
        {
            color?: string;
            id_e?: string;
            name?: string;
        }
    ];
    Users: TUser[];
    created_at?: string;
    description?: string;
    name?: string;
};

export const EquipTab = ({ project, getProject }: TEquipTab) => {
    const [edit, setEdit] = useState<boolean>(false);
    const {
        user: { Permissions: userPerms },
        refreshProject,
    } = useUser();
    const getAllSectors = userPerms.includes("all_sectors");

    const [data, setData] = useState<IProject>();

    const [done, setIsDone] = useState(false);
    const [sectorsData, setSectorsData] = useState<TSector[]>([]);
    const [linkedSectors, setLinkedSectors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [
        isOpenDialogProfileActivityErrors,
        setIsOpenDialogProfileActivityErrors,
    ] = useState(false);

    const [projectTeam, setProjectTeam] = useState<TUser[]>(
        // @ts-ignore
        project.Users ?? []
    );
    const [projectReviewer, setProjectReviewer] = useState<TUser[]>(
        // @ts-ignore
        project.ProjectReviewers ?? []
    );
    const [projectBiller, setProjectBiller] = useState<TUser[]>( // @ts-ignore
        project.ProjectBiller ?? []
    );

    const budgetIdToReq = project.Budget?.id_e ?? "";

    const activiteProfiles = useMemo(
        () =>
            project.Profiles?.map((profile) => ({
                name: profile.name,
                id_e: profile.id_e,
                abbreviation: profile.abbreviation,
                color: colors[Math.floor(Math.random() * colors.length)],
            })) ?? [],
        [project]
    );

    const getSectors = useFetch({
        fn: sectors.getSector,
        start: false,
    });

    const getUsers = useFetch({
        fn: manageUser.getAllUsers,
        start: false,
    });

    const { data: dataBudgetById, isLoading } = useReadBudgetById({
        budgetId: budgetIdToReq,
        start: !!budgetIdToReq,
    });

    const handleEditMode = useCallback(() => {
        setEdit((prev) => !prev);
    }, [edit, setEdit]);

    const handleProfileActivityErrors = useCallback(
        () => setIsOpenDialogProfileActivityErrors((prev) => !prev),
        [isOpenDialogProfileActivityErrors]
    );

    const verifyErrors = useCallback(() => {
        const errorsOnActivityProfile = data?.userProfiles?.some(
            (teamMember) => teamMember.userProcessProfileIds.length === 0
        );

        if (errorsOnActivityProfile) {
            return setIsOpenDialogProfileActivityErrors(true);
        }

        if (linkedSectors.length < 1) {
            return toast.error(
                "Verifique os dados do setor e tente novamente!"
            );
        }

        if (projectTeam.length < 1) {
            return toast.error("Seu time deve possuir ao menos um membro!");
        }
        if (projectReviewer.length < 1) {
            return toast.error("Seu time deve possuir ao menos um revisor!");
        }

        if (projectBiller.length < 1) {
            return toast.error("Seu time deve possuir ao menos um faturador!");
        }
        return true;
    }, [data, linkedSectors, projectTeam, projectReviewer, projectBiller]);

    const storeData = <IProject,>(data: IProject) => {
        getProject.onRefresh({
            projectId: project?.id_e,
            users: true,
            hours: true,
            finished: false,
            sectors: true,
            reviewers: true,
            all: true,
        });
        setData((prevState) => ({ ...prevState, ...data }));
    };

    const handleSubmitProject = async () => {
        const body = {
            userProfiles: data?.userProfiles,
        };
        await updateProjectClosedScope({
            projectId: project.id_e ?? "",
            params: body,
        });
        refreshProject();
    };

    useEffect(() => {
        getSectors.onRefresh({
            all: getAllSectors,
            users: true,
            projects: true,
        });
        getUsers.onRefresh({
            searchPermission: "bill_reports",
        });
    }, []);

    useEffect(() => {
        if (done) {
            setLoading(true);
            if (verifyErrors() === true) handleSubmitProject();
            setTimeout(() => {
                setLoading(false);
                setIsDone(false);
            }, 2000);
        }
    }, [done]);

    useEffect(() => {
        if (getSectors.error) toast.error(`${getSectors.error}`);
        if (getSectors.response) {
            setSectorsData(getSectors.response.sectors);
        }
    }, [getSectors.response]);

    if (budgetIdToReq && isLoading) return <Loading />;

    return (
        <Stack
            sx={{
                py: "2.5rem",
                gap: "2.5rem",
            }}
        >
            {edit ? (
                <CollabsEquipEdit
                    activiteProfiles={activiteProfiles}
                    dataBudgetById={dataBudgetById}
                    loading={loading}
                    projectBiller={projectBiller}
                    projectReviewer={projectReviewer}
                    projectTeam={projectTeam}
                    setIsDone={setIsDone}
                    setLinkedSectors={setLinkedSectors}
                    setProjectBiller={setProjectBiller}
                    setProjectReviewer={setProjectReviewer}
                    setProjectTeam={setProjectTeam}
                    storeData={storeData}
                    project={project}
                    sectorsData={sectorsData}
                    handleEditMode={handleEditMode}
                    getProject={getProject}
                />
            ) : (
                <CollabsEquip
                    projectBiller={projectBiller}
                    projectReviewer={projectReviewer}
                    projectTeam={projectTeam}
                    dataBudgetById={dataBudgetById}
                    handleEditMode={handleEditMode}
                    activiteProfiles={activiteProfiles}
                />
            )}
            <ProfileActivityErrorDialog
                handleClose={handleProfileActivityErrors}
                open={isOpenDialogProfileActivityErrors}
            />
        </Stack>
    );
};
